import React from 'react';
import moment from 'moment';
import { Oval } from 'react-loader-spinner';
import { BsTrash, BsFileEarmarkPdf, BsFileEarmarkImage, BsUpload } from 'react-icons/bs';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { listDriverDocuments, deleteDriverDocument } from '../../../DriversV2/driversV2Slice';
import { triplePSDatasource } from '../../../../datasource';
import { PhotoThumb } from '../../../Orders/components/PhotoThumb/PhotoThumb';
import { base64toBlobPdf } from '../../../../lib/base64ToBlobPdf';
import { DriverDocument, DriverDocumentCategory } from '../../../../models';
import { UploadDocumentModal } from '../UploadDocumentModal/UploadDocumentModal';

import './DocumentsTab.scss';

export interface DocumentsTabProps {
  driverId: string;
}

function categoryLabel(category: DriverDocumentCategory): string {
  switch (category) {
    case 'license': return 'License';
    case 'medical_card': return 'Medical card';
    case 'other': return 'Other';
  }
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

export function DocumentsTab({ driverId }: DocumentsTabProps) {
  const dispatch = useAppDispatch();

  const [uploadModalOpen, setUploadModalOpen] = React.useState(false);

  const driverDocumentsAPI = useAppSelector(state => state.driversV2.driverDocuments);
  const deleteDocumentAPI = useAppSelector(state => state.driversV2.deleteDriverDocument);

  // Fetch on mount
  React.useEffect(() => {
    dispatch(listDriverDocuments(driverId));
  }, [driverId]);

  // Refetch after a successful delete (mirrors PunchCardsTab pattern)
  const prevDeleteLoading = React.useRef(false);
  React.useEffect(() => {
    if (prevDeleteLoading.current && !deleteDocumentAPI.loading && !deleteDocumentAPI.error) {
      dispatch(listDriverDocuments(driverId));
    }
    prevDeleteLoading.current = deleteDocumentAPI.loading;
  }, [deleteDocumentAPI.loading, deleteDocumentAPI.error]);

  const onDelete = (doc: DriverDocument) => {
    if (!window.confirm(`Delete "${doc.label}"?`)) return;
    dispatch(deleteDriverDocument({ driverId, documentId: doc.id }));
  };

  const onOpenPdf = async (doc: DriverDocument) => {
    try {
      const dataUrl = await triplePSDatasource.getDriverDocumentImage(
        driverId,
        doc.id,
        doc.contentType
      );
      // dataUrl is "data:application/pdf;base64,<base64>" — strip the prefix to get raw base64
      const base64 = dataUrl.replace(/^data:[^;]+;base64,/, '');
      const blob = base64toBlobPdf(base64);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl);
    } catch {
      alert('Could not open document.');
    }
  };

  const documents = driverDocumentsAPI.value ?? [];

  // Sort newest-first
  const sorted = documents
    .slice()
    .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());

  const isLoading = driverDocumentsAPI.loading;

  const renderDocumentRow = (doc: DriverDocument) => {
    const imageIcon = isImage(doc.contentType) ? (
      <BsFileEarmarkImage className='doc-icon image' title='Image' />
    ) : (
      <BsFileEarmarkPdf className='doc-icon pdf' title='PDF' />
    );

    const viewControl = isImage(doc.contentType) ? (
      <PhotoThumb
        label='View'
        variant='button'
        triggerClassName='action-button view'
        modalTitle={doc.label}
        fetchImage={() =>
          triplePSDatasource.getDriverDocumentImage(driverId, doc.id, doc.contentType)
        }
      />
    ) : (
      <button
        type='button'
        className='action-button open'
        onClick={() => onOpenPdf(doc)}
      >
        Open
      </button>
    );

    return (
      <div key={doc.id} className='document-row'>
        <div className='doc-icon-col'>{imageIcon}</div>
        <div className='doc-info'>
          <span className='doc-label'>{doc.label}</span>
          <span className={`category-badge category-${doc.category}`}>
            {categoryLabel(doc.category)}
          </span>
          <span className='doc-date'>{moment(doc.createdAt).format('MMM D, YYYY')}</span>
        </div>
        <div className='doc-actions'>
          {viewControl}
          <button
            type='button'
            className='action-button delete'
            onClick={() => onDelete(doc)}
          >
            <BsTrash />
            Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className='documents-tab'>
      <div className='documents-tab-header'>
        <div className='section-title'>Documents</div>
        <button
          type='button'
          className='upload-button'
          onClick={() => setUploadModalOpen(true)}
        >
          <BsUpload style={{ marginRight: 6 }} />
          Upload document
        </button>
      </div>

      {isLoading && (
        <div className='loading-state'>
          <Oval color='#131313' height={32} width={32} secondaryColor='#FBD20A' />
        </div>
      )}

      {!isLoading && sorted.length === 0 && (
        <div className='empty-state'>No documents on file.</div>
      )}

      {!isLoading && sorted.length > 0 && (
        <div className='documents-list'>
          {sorted.map(renderDocumentRow)}
        </div>
      )}

      <UploadDocumentModal
        isOpen={uploadModalOpen}
        driverId={driverId}
        onClose={() => setUploadModalOpen(false)}
      />
    </div>
  );
}
